import {Injectable, OnDestroy} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import {Guid} from 'guid-typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {filter, first, map} from 'rxjs/operators';
import {Attachment, AttachmentData, AttachmentSource} from './attachment';
import {AttachmentApiService} from './attachment-api.service';
import {AttachmentDbService} from './attachment-db.service';
import {AttachmentLoadingTypeEnum} from './attachment-loading-type.enum';
import {AttachmentType} from './attachment-type';
import {firstValueFrom, Subject} from 'rxjs';
import {Activity} from '@docma-mm-core/activity/activity';
import {Fault, ViewFault} from '@docma-mm-core/fault/fault';
import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {User} from '@docma-mm-core/user/user';

@UntilDestroy()
@Injectable()
export class AttachmentService implements OnDestroy {

  private user: User;

  private attachmentsAddedSubject = new Subject<{faultAttachments: Attachment[], activityAttachments: Attachment[], fault: Fault, activity: Activity}>();
  onAttachmentsAdded =  this.attachmentsAddedSubject.asObservable();

  private attachmentChangedSubject = new Subject<{attachment: Attachment, fault: Fault, activity: Activity}>();
  onAttachmentChanged =  this.attachmentChangedSubject.asObservable();

  private attachmentDeletedSubject = new Subject<{attachment: Attachment, fault: Fault, activity: Activity, modifiedOn: Date, modifier: User}>();
  onAttachmentDeleted =  this.attachmentDeletedSubject.asObservable();

  constructor(private readonly apiService: AttachmentApiService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private dbService: AttachmentDbService,
              private readonly sanitizer: DomSanitizer) {

    this.projectLoaderService.projectUserPermissions
      .pipe(untilDestroyed(this))
      .subscribe(u => this.user = u.data.User);
  }

  ngOnDestroy(): void {
  }

  createNewAttachment(data?: AttachmentData, file?: File, attachmentType: AttachmentType = AttachmentType.Picture ) {
    const createDate = new Date(Date.now());
    const attachment = new Attachment();
    attachment.loadState = new LoadState({ status: LoadStatusEnum.Loaded, data: {}, date: createDate });
    attachment.syncState = new SyncState({ status: SyncStatusEnum.NotSync, date: createDate });
    const changeData = new AttachmentData(data);
    changeData.UniqueId = Guid.create().toString();
    changeData.SUid =  Guid.create().toString();

    if (this.user) {
      changeData.Creator = {Account: this.user.Account, FirstName: this.user.FirstName, LastName: this.user.LastName };
    }

    changeData.Type = attachmentType;
    changeData.EditableFor = ViewPoint.Contractor | ViewPoint.Customer | ViewPoint.Subcontractor;
    changeData.VisibleFor = ViewPoint.Contractor | ViewPoint.Customer | ViewPoint.Subcontractor;
    changeData.CreatedOn = changeData.CreatedOn ?? createDate;
    if (file) {
      Object.assign(changeData, Attachment.getDataFromFile(file));
      this.setAttachmentSource(attachment.source, file);
      if(!changeData.Description) {
        changeData.Description = file.name;
      }
    }
    attachment.changeState = new ChangeState({ data: changeData, status: ChangeStatusEnum.New, date: createDate });
    return attachment;
  }

  //make private
  setAttachmentSource(attachmentSource: AttachmentSource, file: File) {
    if (file && file.size > 0) {
      const urlCreator = window.URL;
      const url = urlCreator.createObjectURL(file);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
      attachmentSource.url = safeUrl;
      attachmentSource.name = file.name;
      attachmentSource.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: file});
    }
  }

  async loadAttachmentSource(attachment: Attachment, attachmentSource: AttachmentSource,
                             extendedContentDisposition: boolean = false,
                             size?: { height: number, width: number }): Promise<AttachmentSource> {

    attachmentSource.loadState = new LoadState({ status: LoadStatusEnum.Loading });

    try {
      const file = await this.apiService.getAttachment(attachment.id, extendedContentDisposition, size && size.width, size && size.height);
      this.setAttachmentSource(attachmentSource, file);
    } catch (error) {
      attachmentSource.loadState = new LoadState({ status: LoadStatusEnum.Error, error: `Can't load from server`});
    }
    return attachmentSource;
  }

  private async loadAttachmentSourceFromDb(projectMydocmaUid: string, attachment: Attachment, attachmentSource: AttachmentSource,
                                           size?: { height: number, width: number }): Promise<AttachmentSource> {

    attachmentSource.loadState = new LoadState({ status: LoadStatusEnum.Loading });

    try {
      const file = await this.dbService.getFile(projectMydocmaUid, attachment.uid);
      if (file) {
        this.setAttachmentSource(attachmentSource, file);
      } else {
        attachmentSource.loadState = new LoadState({ status: LoadStatusEnum.Error, error: `Not found in db`});
      }
    } catch (error) {
      attachmentSource.loadState = new LoadState({ status: LoadStatusEnum.Error, error: `Can't load from db`});
    }
    return attachmentSource;
    }


  // Gets attachment thumbnail from cache or loads it from server
  async loadAttachmentThumbnail(projectMydocmaUid: string, attachment: Attachment,
                                size?: { height: number, width: number },
                                loadingType: AttachmentLoadingTypeEnum = AttachmentLoadingTypeEnum.FromServer,
                                force: boolean = false): Promise<AttachmentSource> {

    if (!size) {
      size = { height: 128, width: 128 };
    }

    let thumbnail: AttachmentSource;

    if (!force) {
      if (attachment.thumbnail && attachment.thumbnail.loadState.status === LoadStatusEnum.Loaded) {
        thumbnail =  attachment.thumbnail;
      }
      else if (attachment.source && attachment.source.loadState.status === LoadStatusEnum.Loaded) {
        thumbnail = await getThumbnail(attachment, this.sanitizer);
      }

      if (!thumbnail) {
        if (attachment.thumbnail && attachment.thumbnail.loadState.status !== LoadStatusEnum.NotLoaded) {
          thumbnail = attachment.thumbnail;
        }
        else if (attachment.source && attachment.source.loadState.status !== LoadStatusEnum.NotLoaded) {
          thumbnail = await getThumbnail(attachment, this.sanitizer);
        }
      }

      if (thumbnail) {
        attachment.thumbnail = thumbnail;
        return thumbnail;
      }
    }


    if (loadingType === AttachmentLoadingTypeEnum.FromServer) {
      // Don't wait for loading
      thumbnail = attachment.thumbnail || new AttachmentSource();
      this.loadAttachmentSource(attachment, thumbnail, false, size);
    }
    else if (loadingType === AttachmentLoadingTypeEnum.FromDb) {
      thumbnail = attachment.source || new AttachmentSource();
      this.loadAttachmentSourceFromDb(projectMydocmaUid, attachment, thumbnail);
    }
    attachment.thumbnail = thumbnail;
    return attachment.thumbnail;
  }
  // Gets attachment source from cache or loads it from server
  async getAttachmentSource(attachment: Attachment, extendedContentDisposition: boolean = false, size?: { height: number, width: number }): Promise<AttachmentSource> {

    // TODO IN NEXT SPRINT
    // TAKE SEARCH_ID FROM ACTIVITY
    // if (attachment.data.Type === AttachmentType.Writing && attachment.data?.OriginalFileName != null && attachment.data?.OriginalFileName !== '') {
    // }
    // else if (attachment.data?.Description != null && attachment.data.Description !== '') {
    // }


    if (attachment.source && attachment.source.loadState.status !== LoadStatusEnum.NotLoaded) {
      return attachment.source;
    }

    attachment.source = attachment.source || new AttachmentSource();
    // Don't wait for loading
    this.loadAttachmentSource(attachment, attachment.source, extendedContentDisposition, size);
    return attachment.source;
  }

  openExtensions: string[] = ['jpg', 'jpeg', 'jfif', 'gif', 'bmp', 'png', 'tif', 'tiff', 'eps', 'pdf'];

  async downloadAttachment(attachment: Attachment) {

    const source = await this.getAttachmentSource(attachment, true);

    await source.loadState$.pipe(filter(ls => ls.status === LoadStatusEnum.Loaded || ls.status === LoadStatusEnum.Error  ), first())
    .toPromise();

    const downloadFileName: string = source?.name ?? attachment.data.FileName;
    const extension: string = downloadFileName.split('.').pop();

    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(source.data, downloadFileName);
    }
    else {
      const anchor = document.createElement('a');
      anchor.download = downloadFileName;
      anchor.href = (source.url as any).changingThisBreaksApplicationSecurity;
      anchor.type = attachment.data.MimeType;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      if (this.openExtensions.findIndex(x => x.toLowerCase() === extension.toLowerCase() ) !== -1) {
        window.open(anchor.href, '_blank');
      }
    }
  }

  async addAttachments(files: File[], attachmentData: AttachmentData, fault: ViewFault, activity: Activity): Promise<boolean> {
    const newFaultAttachments: Attachment[] = [];
    const newActivityAttachments: Attachment[] = [];

    files.forEach(f => {
      const attachment = this.createNewAttachment(attachmentData,f);
      if (fault && !activity) {
        fault.addAttachment(attachment);
        newFaultAttachments.push(attachment);
      } else if (activity) {
        activity.addAttachment(attachment);
        newActivityAttachments.push(attachment);
      }
    });

    if (!fault.isNew) {
      this.attachmentsAddedSubject.next({faultAttachments: newFaultAttachments, activityAttachments: newActivityAttachments, fault, activity});
      return true;
    }
    return false;
  }

  async changeAttachment(attachment: Attachment, fault: ViewFault) {
    const activity = (attachment.activity ? fault.activities.find(f => f.uid === attachment.activity.uid): null);
    this.attachmentChangedSubject.next({attachment, fault, activity});
  }

  async deleteAttachment(attachment: Attachment, fault: ViewFault, activity: Activity) {
    const modifiedOn = await this.apiService.deleteAttachments([attachment]).toPromise();

    if(activity) {
      fault.removeAttachmentFromActivity(attachment, activity);
    } else {
      fault.removeAttachment(attachment);
    }

    this.attachmentDeletedSubject.next({attachment, fault, activity, modifiedOn, modifier: this.user});
    return modifiedOn;
  }


}

async function getThumbnail(attachment: Attachment, sanitizer: DomSanitizer) {
  const isImage = attachment.source.data.type?.startsWith('image');
    // && !attachment.source.data.type?.startsWith('application/octet-stream')) && !attachment.source.name?.endsWith('msg');
  if (!isImage) {
    // attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: attachment.thumbnail.data});
    return Promise.resolve(attachment.thumbnail);
  }

  const blob = await resizeImage(attachment.source.loadState.data, 600, 600);
  // const attachmentData = attachment.changeState.data;
  attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded});
  const file = new File([blob], attachment.data.Description,{ type: 'image/png', lastModified: attachment.changeState.date.getTime() });

  if (file && file.size > 0) {
    const urlCreator = window.URL;
    const url = urlCreator.createObjectURL(file);
    const safeUrl = sanitizer.bypassSecurityTrustUrl(url);
    attachment.thumbnail.url = safeUrl;
    attachment.thumbnail.name = file.name;
    attachment.thumbnail.loadState = new LoadState({status: LoadStatusEnum.Loaded, data: file});
  }

  return attachment.thumbnail;
}
function resizeImage(file: File, maxWidth:number, maxHeight:number):Promise<Blob> {
  return new Promise((resolve, reject) => {
    let image = new Image();
    image.src = URL.createObjectURL(file);
    image.onload = () => {
      let width = image.width;
      let height = image.height;

      if (width <= maxWidth && height <= maxHeight) {
        resolve(file);
      }

      let newWidth;
      let newHeight;

      if (width > height) {
        newHeight = height * (maxWidth / width);
        newWidth = maxWidth;
      }
      else {
        newWidth = width * (maxHeight / height);
        newHeight = maxHeight;
      }

      let canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      let context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, newWidth, newHeight);
      canvas.toBlob(resolve, 'image/png');
    };
    image.onerror = reject;
  });
}

